import React, { useState, useEffect, useRef, useMemo } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faClose,
  faCheckCircle,
  faCancel,
  faReceipt,
  faTrash,
  faSpinner,
  faCheck,
  faCashRegister,
  faPlus,
  faBarcode,
  faMinus,
  faSearch,
} from "@fortawesome/free-solid-svg-icons";
import useSWR, { mutate } from "swr";
import DashboardContenLeftMenus from "./DashboardContenLeftMenus";
import DashboardContentHeader from "./DashboardContentHeader";
import DashboardContentFooter from "./DashboardContentFooter";
import TopActionMenus from "./TopActionMenus";
import MessageModal from "./MessageModal";
import SecureLS from "secure-ls";
import { useNavigationEvent } from "navigation-react";
import defaultProductImg from "./images/product.png";

const ls = new SecureLS({ encodingType: "aes" });

const username = "admin1";
const password = "abc@123@Q";
const authHeader = "Basic " + btoa(`${username}:${password}`);

// Function to fetch CSRF token - NO Basic Auth for this endpoint
const fetchCsrfToken = async () => {
  try {
    const response = await fetch(
      `${process.env.REACT_APP_BASE_URL}/session/token`,
      {
        method: "GET",
        credentials: "include",
        headers: {
          Accept: "text/plain",
        },
      },
    );

    if (!response.ok) {
      throw new Error(`Failed to fetch CSRF token: ${response.status}`);
    }

    const token = await response.text();
    return token.trim();
  } catch (error) {
    console.error("Error fetching CSRF token:", error);
    throw error;
  }
};

// Function to get cached token or fetch new one
const getCsrfToken = async (forceRefresh = false) => {
  try {
    if (!forceRefresh) {
      const cachedToken = ls.get("usercsrf_token");
      if (cachedToken && cachedToken.trim()) {
        return cachedToken.trim();
      }
    }

    const freshToken = await fetchCsrfToken();
    ls.set("usercsrf_token", freshToken);
    return freshToken;
  } catch (error) {
    console.error("Error getting CSRF token:", error);
    const cachedToken = ls.get("usercsrf_token");
    if (cachedToken) {
      return cachedToken;
    }
    throw error;
  }
};

// Updated fetcher for GET requests
const fetcher = async (url) => {
  try {
    let csrfToken = ls.get("usercsrf_token");

    if (!csrfToken) {
      csrfToken = await fetchCsrfToken();
      ls.set("usercsrf_token", csrfToken);
    }

    const headers = {
      Authorization: authHeader,
      Accept: "application/json",
      "Content-Type": "application/json",
    };

    if (csrfToken) {
      headers["X-CSRF-Token"] = csrfToken;
    }

    const response = await fetch(url, {
      credentials: "include",
      headers: headers,
    });

    if (!response.ok) {
      throw new Error(`API request failed with status ${response.status}`);
    }

    return response.json();
  } catch (error) {
    console.error("Fetch error:", error);
    throw error;
  }
};

// Function to deduplicate products based on product_uuid
const deduplicateProducts = (products) => {
  if (!products || !Array.isArray(products)) return [];

  const seen = new Map();

  for (const product of products) {
    const uuid =
      product.product_uuid || product.field_product_image_uuid || product.id;

    if (!seen.has(uuid)) {
      seen.set(uuid, product);
    }
  }

  return Array.from(seen.values());
};

const paymentOptions = [
  { value: "cash", label: "Cash" },
  { value: "credit", label: "Credit" },
  { value: "mobile_money", label: "Mobile Money" },
  { value: "card", label: "Card" },
  { value: "bank_transfer", label: "Bank Transfer" },
];

export default function SalesOrder() {
  const { stateNavigator } = useNavigationEvent();
  const cashierUid = ls.get("currentuserid");

  const [loading, setLoading] = useState(false);
  const [customer, setCustomer] = useState("");
  const [customerName, setCustomerName] = useState("");
  const [customerSearchTerm, setCustomerSearchTerm] = useState("");
  const [orderNumber, setOrderNumber] = useState("");
  const [paymentType, setPaymentType] = useState("cash");
  const [amountPaid, setAmountPaid] = useState("");
  const [discount, setDiscount] = useState("");
  const [paymentReference, setPaymentReference] = useState("");
  const [items, setItems] = useState([]);
  const [notes, setNotes] = useState("");
  const [barcode, setBarcode] = useState("");
  const [productSearchTerm, setProductSearchTerm] = useState("");
  const [modalSuccessMsg, setModalSuccessMsg] = useState(false);
  const [modalError, setModalError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [showCustomerModal, setShowCustomerModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");

  const [newCustomerForm, setNewCustomerForm] = useState({
    name: "",
    phone: "",
    email: "",
    address: "",
  });

  const barcodeInputRef = useRef();
  const customerInputRef = useRef();

  const subtotal = items.reduce((sum, i) => sum + i.price * i.quantity, 0);
  const discountAmount = parseFloat(discount) || 0;
  const totalAmount = subtotal - discountAmount;
  const paidAmount = parseFloat(amountPaid) || 0;
  const remainingBalance = totalAmount - paidAmount;

  const { data: customers } = useSWR(
    `${process.env.REACT_APP_CUSTOMER_API}?fields[node--customer]=title,field_phone_number&page[limit]=1000`,
    fetcher,
  );

  // Direct product fetch - API returns flat array
  const { data: productsRaw } = useSWR(
    `${process.env.REACT_APP_PRODUCT_LIST_API}`,
    fetcher,
  );

  // Deduplicate products at the data level
  const products = useMemo(() => {
    const uniqueProducts = deduplicateProducts(productsRaw);

    // Log warning if duplicates were found
    if (productsRaw && productsRaw.length !== uniqueProducts.length) {
      console.warn(
        `Duplicates removed: ${productsRaw.length} -> ${uniqueProducts.length} products`,
      );
    }

    return uniqueProducts;
  }, [productsRaw]);

  const filteredCustomers =
    customers?.data?.filter(
      (c) =>
        c.attributes.title
          .toLowerCase()
          .includes(customerSearchTerm.toLowerCase()) ||
        c.attributes.field_phone_number
          ?.toLowerCase()
          .includes(customerSearchTerm.toLowerCase()),
    ) || [];

  // Filter products with deduplication already handled
  const filteredProducts = useMemo(() => {
    if (!products) return [];

    return products.filter(
      (p) =>
        p.title.toLowerCase().includes(productSearchTerm.toLowerCase()) ||
        p.field_sku.toLowerCase().includes(productSearchTerm.toLowerCase()),
    );
  }, [products, productSearchTerm]);

  useEffect(() => {
    const initCsrf = async () => {
      try {
        const token = await fetchCsrfToken();
        ls.set("usercsrf_token", token);
      } catch (error) {
        console.error("Failed to get initial CSRF token:", error);
        setErrorMessage(
          "Failed to initialize session. Please refresh the page.",
        );
        setModalError(true);
      }
    };
    initCsrf();

    customerInputRef.current?.focus();
    setOrderNumber(`PS-${Date.now()}-${Math.floor(Math.random() * 1000)}`);
  }, []);

  // Debug effect to check for duplicates in the original data
  useEffect(() => {
    if (productsRaw && productsRaw.length > 0) {
      const uuids = productsRaw.map(
        (p) => p.product_uuid || p.field_product_image_uuid || p.id,
      );
      const uniqueUuids = new Set(uuids);
      if (uuids.length !== uniqueUuids.size) {
        console.warn("Duplicate UUIDs detected in API response!");
        console.warn("Total products:", uuids.length);
        console.warn("Unique products:", uniqueUuids.size);

        // Find which UUIDs are duplicated
        const duplicateUuids = uuids.filter(
          (id, index) => uuids.indexOf(id) !== index,
        );
        const uniqueDuplicates = [...new Set(duplicateUuids)];
        console.warn("Duplicate UUIDs:", uniqueDuplicates);
      }
    }
  }, [productsRaw]);

  const isOutOfStock = (product) =>
    Number(product?.field_stock_quantity || 0) <= 0;

  const handleAddProductToCart = (product) => {
    // Use product_uuid from the API response
    const uuid =
      product.product_uuid || product.field_product_image_uuid || product.id;

    if (!uuid) {
      setErrorMessage(`${product.title} has no UUID and cannot be added`);
      setModalError(true);
      return;
    }

    if (!product.field_sku) {
      setErrorMessage(`${product.title} has no SKU and cannot be added`);
      setModalError(true);
      return;
    }

    setItems((prev) => {
      const existing = prev.find((i) => i.product_uuid === uuid);

      if (existing) {
        return prev.map((i) =>
          i.product_uuid === uuid ? { ...i, quantity: i.quantity + 1 } : i,
        );
      }

      return [
        ...prev,
        {
          product_uuid: uuid, // Store the actual UUID for API
          title: product.title,
          sku: product.field_sku,
          price: Number(product.field_price),
          quantity: 1,
          imageUrl:
            product.field_product_image && product.field_product_image !== ""
              ? product.field_product_image.startsWith("http")
                ? product.field_product_image
                : `${process.env.REACT_APP_BASE_URL}${product.field_product_image}`
              : defaultProductImg,
        },
      ];
    });
  };

  const updateQuantity = (product_uuid, qty) => {
    const quantity = parseInt(qty, 10);
    if (isNaN(quantity) || quantity < 1) return;

    setItems((prev) =>
      prev.map((i) =>
        i.product_uuid === product_uuid ? { ...i, quantity } : i,
      ),
    );
  };

  const removeItem = (product_uuid) => {
    setItems((prev) => prev.filter((i) => i.product_uuid !== product_uuid));
  };

  const handleBarcodeScan = (code) => {
    const product = products?.find(
      (p) => p.field_gtin === code || p.field_sku === code,
    );

    if (!product) {
      setErrorMessage(`Product "${code}" not found`);
      setModalError(true);
      return;
    }

    if (isOutOfStock(product)) {
      setErrorMessage(`Insufficient stock for ${product.title}`);
      setModalError(true);
      return;
    }

    handleAddProductToCart(product);
    setBarcode("");
    barcodeInputRef.current?.focus();
  };

  const handleAddNewCustomer = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const csrfToken = await getCsrfToken(true);

      if (!csrfToken) {
        throw new Error("Could not obtain CSRF token");
      }

      const customerAttributes = {
        title: newCustomerForm.name || "Unknown Customer",
        field_phone_number: newCustomerForm.phone || "",
        field_customer_email: newCustomerForm.email || "",
        field_customer_address: newCustomerForm.address || "",
      };

      const response = await fetch(process.env.REACT_APP_CUSTOMER_API, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/vnd.api+json",
          Accept: "application/vnd.api+json",
          "X-CSRF-Token": csrfToken,
        },
        body: JSON.stringify({
          data: {
            type: "node--customer",
            attributes: customerAttributes,
          },
        }),
      });

      const responseText = await response.text();
      let json;
      try {
        json = JSON.parse(responseText);
      } catch (parseError) {
        setErrorMessage(`Server returned: ${responseText.substring(0, 200)}`);
        setModalError(true);
        setLoading(false);
        return;
      }

      if (!response.ok) {
        const errorDetail =
          json.errors?.[0]?.detail || json.message || `HTTP ${response.status}`;
        setErrorMessage(errorDetail);
        setModalError(true);
        setLoading(false);
        return;
      }

      setCustomer(json.data.id);
      setCustomerName(json.data.attributes.title);
      setShowCustomerModal(false);
      setNewCustomerForm({ name: "", phone: "", email: "", address: "" });

      mutate(
        `${process.env.REACT_APP_CUSTOMER_API}?fields[node--customer]=title,field_phone_number&page[limit]=1000`,
      );

      setLoading(false);
    } catch (error) {
      console.error("Add customer error:", error);
      setErrorMessage(error.message || "Network error. Please try again.");
      setModalError(true);
      setLoading(false);
    }
  };

  const handleAddSales = async () => {
    // Validate customer
    if (!customer) {
      setErrorMessage("Please select a customer");
      setModalError(true);
      return;
    }

    // Validate items
    if (!items.length) {
      setErrorMessage("Please add at least one item to the cart");
      setModalError(true);
      return;
    }

    // Validate items have UUIDs
    const invalidItems = items.filter((item) => !item.product_uuid);
    if (invalidItems.length > 0) {
      setErrorMessage(
        `Missing UUID for: ${invalidItems.map((i) => i.title).join(", ")}`,
      );
      setModalError(true);
      return;
    }

    // Validate payment amount
    if (!amountPaid || amountPaid === "" || parseFloat(amountPaid) < 0) {
      setErrorMessage("Please enter a valid payment amount");
      setModalError(true);
      return;
    }

    // Validate discount
    if (discountAmount < 0) {
      setErrorMessage("Discount cannot be negative");
      setModalError(true);
      return;
    }

    if (discountAmount > subtotal) {
      setErrorMessage("Discount cannot exceed subtotal");
      setModalError(true);
      return;
    }

    if (paidAmount > totalAmount) {
      setErrorMessage("Amount paid cannot exceed total amount");
      setModalError(true);
      return;
    }

    if (remainingBalance > 0) {
      const confirmPartial = window.confirm(
        `This is a partial payment. Balance due: GHC${remainingBalance.toFixed(
          2,
        )}\nContinue?`,
      );
      if (!confirmPartial) {
        return;
      }
    }

    setLoading(true);
    try {
      const csrfToken = await getCsrfToken(true);

      if (!csrfToken) {
        throw new Error("Could not obtain CSRF token");
      }

      // Prepare order data with proper UUIDs
      const orderData = {
        order_number: orderNumber,
        customer_nid: customer,
        cashier_uid: cashierUid,
        payment_type: paymentType,
        amount_paid: paidAmount,
        discount: discountAmount,
        payment_reference: paymentReference,
        notes: notes,
        items: items.map((i) => ({
          product_uuid: i.product_uuid, // Now using the correct UUID
          qty: Number(i.quantity),
          unit_price: Number(i.price),
        })),
      };

      console.log("Order data:", JSON.stringify(orderData, null, 2));

      const response = await fetch(
        `${process.env.REACT_APP_BASE_URL}/pos/checkout`,
        {
          method: "POST",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
            "X-CSRF-Token": csrfToken,
          },
          body: JSON.stringify(orderData),
        },
      );

      const responseText = await response.text();
      let json;
      try {
        json = JSON.parse(responseText);
        console.log("Server response:", JSON.stringify(json, null, 2));
        // const orderUuid = json.order_uuid;
      } catch (parseError) {
        setErrorMessage(`Server returned: ${responseText.substring(0, 200)}`);
        setModalError(true);
        setLoading(false);
        return;
      }

      if (!response.ok) {
        setErrorMessage(json.message || "Failed to create order");
        setModalError(true);
        setLoading(false);
        return;
      }

      let message = "Order created successfully! ";
      if (discountAmount > 0) {
        message += `(Discount Applied: GHC${discountAmount.toFixed(2)}) `;
      }
      if (remainingBalance === 0) {
        message += "✅ Fully Paid";
      } else if (remainingBalance > 0) {
        message += `⚠️ Partial Payment - Balance Due: GHC${remainingBalance.toFixed(
          2,
        )}`;
      }

      setSuccessMessage(message);
      setModalSuccessMsg(true);

      setTimeout(() => {
        stateNavigator.navigate("viewsalesinvoicedetails", {
          salesId: json.order_uuid,
        });
      }, 3000);
    } catch (error) {
      console.error("Error creating order:", error);
      setErrorMessage(error.message || "Network error");
      setModalError(true);
    }
    setLoading(false);
  };

  const resetForm = () => {
    setCustomer("");
    setCustomerName("");
    setItems([]);
    setNotes("");
    setBarcode("");
    setCustomerSearchTerm("");
    setProductSearchTerm("");
    setPaymentType("cash");
    setAmountPaid("");
    setDiscount("");
    setPaymentReference("");
    setSuccessMessage("");
    setModalSuccessMsg(false);
    setOrderNumber(`PS-${Date.now()}-${Math.floor(Math.random() * 1000)}`);
  };

  const getBalanceStyle = () => {
    return remainingBalance > 0 ? styles.balanceDue : styles.balancePaid;
  };

  const getCheckoutBtnStyle = () => {
    const baseStyle = styles.checkoutBtn;
    if (loading || items.length === 0 || !customer) {
      return { ...baseStyle, ...styles.disabled };
    }
    return baseStyle;
  };

  return (
    <div className="dashboard-container">
      <DashboardContentHeader />
      <div className="content-wrapper">
        <DashboardContenLeftMenus />
        <div className="sales-container" style={styles.container}>
          <TopActionMenus
            AddTitle="Add Sales"
            buttonLabel="Sales Register"
            icon={faReceipt}
            onClickIcon={faCashRegister}
            onClick={() => stateNavigator.navigate("viewsalesregister")}
          />

          <div style={styles.mainContainer}>
            {/* Right COLUMN - Order Form */}
            <div style={styles.leftColumn}>
              <div style={styles.section}>
                <div style={styles.grid2cols}>
                  <div>
                    <label style={styles.label}>Invoice Number</label>
                    <input
                      type="text"
                      style={styles.input}
                      value={orderNumber}
                      readOnly
                    />
                  </div>
                  <div>
                    <label style={styles.label}>Payment Type *</label>
                    <select
                      style={styles.input}
                      value={paymentType}
                      onChange={(e) => setPaymentType(e.target.value)}
                      required
                    >
                      {paymentOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              <div style={styles.section}>
                <label style={styles.label}>Customer *</label>
                <div style={styles.rowWithButton}>
                  <div style={styles.searchWrapper}>
                    <input
                      type="text"
                      ref={customerInputRef}
                      style={styles.input}
                      placeholder="Search customer..."
                      value={customerName || customerSearchTerm}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (customerName && value !== customerName) {
                          setCustomer("");
                          setCustomerName("");
                        }
                        setCustomerSearchTerm(value);
                      }}
                      required
                    />
                    {!customerName &&
                      customerSearchTerm &&
                      filteredCustomers.length > 0 && (
                        <div style={styles.searchResults}>
                          {filteredCustomers.map((cust) => (
                            <div
                              key={cust.id}
                              style={styles.searchItem}
                              onClick={() => {
                                setCustomer(cust.id);
                                setCustomerName(cust.attributes.title);
                                setCustomerSearchTerm("");
                              }}
                            >
                              <div style={styles.searchItemName}>
                                {cust.attributes.title}
                              </div>
                              <div style={styles.searchItemPhone}>
                                {cust.attributes.field_phone_number ||
                                  "No phone number"}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                  </div>
                  <button
                    style={styles.iconButton}
                    onClick={() => setShowCustomerModal(true)}
                    type="button"
                  >
                    <FontAwesomeIcon icon={faPlus} color="#FFF" size={18} />
                  </button>
                </div>
              </div>

              <div style={styles.section}>
                <label style={styles.label}>Barcode Scanner</label>
                <div style={styles.rowWithButton}>
                  <input
                    type="text"
                    ref={barcodeInputRef}
                    style={styles.input}
                    placeholder="Scan barcode or enter manually"
                    value={barcode}
                    onChange={(e) => setBarcode(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && barcode.trim()) {
                        handleBarcodeScan(barcode.trim());
                      }
                    }}
                  />
                  <button
                    style={styles.iconButton}
                    onClick={() => {
                      if (barcode.trim()) {
                        handleBarcodeScan(barcode.trim());
                      }
                    }}
                  >
                    <FontAwesomeIcon icon={faBarcode} color="#FFF" />
                  </button>
                </div>
              </div>

              <div style={styles.row2cols}>
                <div style={styles.blueBox}>
                  <label style={styles.boxLabel}>Amount Paid (GHC) *</label>
                  <input
                    type="number"
                    style={styles.numberInputAmount}
                    value={amountPaid}
                    onChange={(e) => setAmountPaid(e.target.value)}
                    placeholder="0.00"
                    step="0.01"
                    min="0"
                  />
                </div>
                <div style={styles.orangeBox}>
                  <label style={styles.boxLabel}>Discount (GHC)</label>
                  <input
                    type="number"
                    style={styles.numberInputDiscount}
                    value={discount}
                    onChange={(e) => setDiscount(e.target.value)}
                    placeholder="0.00"
                    step="0.01"
                    min="0"
                  />
                </div>
              </div>

              {paymentType !== "cash" && (
                <div style={styles.section}>
                  <label style={styles.label}>Transaction Reference *</label>
                  <input
                    type="text"
                    style={styles.input}
                    value={paymentReference}
                    onChange={(e) => setPaymentReference(e.target.value)}
                    placeholder="e.g., MOMO123456"
                  />
                </div>
              )}

              <div style={styles.summaryCard}>
                <div style={styles.summaryRow}>
                  <span>Subtotal:</span>
                  <span style={styles.price}>GHC {subtotal.toFixed(2)}</span>
                </div>
                {discountAmount > 0 && (
                  <div style={styles.summaryRow}>
                    <span style={{ color: "#ff9800" }}>Discount:</span>
                    <span style={{ color: "#ff9800", fontWeight: "bold" }}>
                      - GHC {discountAmount.toFixed(2)}
                    </span>
                  </div>
                )}
                <div style={{ ...styles.summaryRow, ...styles.totalRow }}>
                  <span style={styles.totalLabel}>Total:</span>
                  <span style={styles.totalValue}>
                    GHC {totalAmount.toFixed(2)}
                  </span>
                </div>
                <div style={{ ...styles.summaryRow, ...styles.balanceRow }}>
                  <span style={styles.balanceLabel}>Balance:</span>
                  <span style={getBalanceStyle()}>
                    GHC {remainingBalance.toFixed(2)}
                  </span>
                </div>
              </div>

              <div style={styles.actionRow}>
                <button style={styles.cancelBtn} onClick={resetForm}>
                  <FontAwesomeIcon icon={faClose} color="#FFF" />
                  <span style={styles.btnText}>Clear</span>
                </button>
                <button
                  style={getCheckoutBtnStyle()}
                  onClick={handleAddSales}
                  disabled={loading || items.length === 0 || !customer}
                >
                  {loading ? (
                    <FontAwesomeIcon
                      icon={faSpinner}
                      spin={true}
                      color="#FFF"
                    />
                  ) : (
                    <>
                      <FontAwesomeIcon icon={faCheck} color="#FFF" />
                      <span style={styles.checkoutText}>
                        Pay GHC {paidAmount.toFixed(2)}
                      </span>
                    </>
                  )}
                </button>
              </div>
            </div>

            {/* Left COLUMN - Products & Cart */}
            <div style={styles.rightColumn}>
              {/* Product Grid */}
              <div style={styles.productsSection}>
                <div style={styles.productSearchHeader}>
                  <div style={styles.searchIconWrapper}>
                    <FontAwesomeIcon
                      icon={faSearch}
                      style={styles.searchIcon}
                    />
                  </div>
                  <input
                    type="text"
                    style={styles.productSearchInput}
                    placeholder="Search products by name or SKU..."
                    value={productSearchTerm}
                    onChange={(e) => setProductSearchTerm(e.target.value)}
                    autoFocus
                  />
                  {productSearchTerm && (
                    <button
                      style={styles.clearSearchBtn}
                      onClick={() => setProductSearchTerm("")}
                    >
                      <FontAwesomeIcon icon={faClose} size={16} />
                    </button>
                  )}
                </div>

                <div style={styles.productsGrid}>
                  {filteredProducts.length > 0 ? (
                    filteredProducts.map((product) => {
                      const isOutOfStockItem = isOutOfStock(product);
                      const imageUrl =
                        product.field_product_image &&
                        product.field_product_image !== ""
                          ? product.field_product_image.startsWith("http")
                            ? product.field_product_image
                            : `${process.env.REACT_APP_BASE_URL}${product.field_product_image}`
                          : defaultProductImg;

                      const hasUuid = !!(
                        product.product_uuid ||
                        product.field_product_image_uuid ||
                        product.id
                      );
                      const canAddToCart =
                        !isOutOfStockItem && product.field_sku && hasUuid;

                      return (
                        <div
                          key={
                            product.product_uuid ||
                            product.field_product_image_uuid ||
                            product.id
                          }
                          style={styles.productCard}
                          onClick={() =>
                            canAddToCart && handleAddProductToCart(product)
                          }
                        >
                          <div style={styles.productImageContainer}>
                            <img
                              src={imageUrl}
                              alt={product.title}
                              style={styles.productImage}
                              onError={(e) => {
                                e.target.src = defaultProductImg;
                              }}
                            />
                            {isOutOfStockItem && (
                              <div style={styles.outOfStockOverlay}>
                                <span style={styles.outOfStockText}>
                                  Out of Stock
                                </span>
                              </div>
                            )}
                            <div style={styles.productContent}>
                              <div style={styles.productName}>
                                {product.title}
                              </div>
                              <div style={styles.productSku}>
                                SKU: {product.field_sku}
                              </div>
                              <div style={styles.productFooter}>
                                <div>
                                  <div style={styles.productPrice}>
                                    GHC{" "}
                                    {Number(product.field_price || 0).toFixed(
                                      2,
                                    )}
                                  </div>
                                  <div style={styles.productStock}>
                                    Stock: {product.field_stock_quantity || 0}
                                  </div>
                                </div>
                                {canAddToCart && (
                                  <button
                                    style={styles.addToCartBtn}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleAddProductToCart(product);
                                    }}
                                  >
                                    <FontAwesomeIcon
                                      icon={faPlus}
                                      color="#FF6B35"
                                      size={20}
                                    />
                                  </button>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })
                  ) : (
                    <div style={styles.noProducts}>
                      {productSearchTerm
                        ? "No products match your search"
                        : "No products found"}
                    </div>
                  )}
                </div>
              </div>

              {/* Cart Section - Your Order */}
              <div style={styles.cartSection}>
                <div style={styles.cartHeader}>
                  Your Order ({items.length} items)
                </div>
                {items.length === 0 ? (
                  <div style={styles.emptyCart}>
                    <div style={styles.emptyText}>No items added</div>
                  </div>
                ) : (
                  <div style={styles.cartItemsList}>
                    {items.map((item) => (
                      <div key={item.product_uuid} style={styles.cartItem}>
                        <div style={styles.cartItemInfo}>
                          <div style={styles.cartImage}>
                            <img
                              src={item.imageUrl}
                              alt={item.title}
                              style={styles.cartImageImg}
                              onError={(e) => {
                                e.target.src = defaultProductImg;
                              }}
                            />
                          </div>
                          <div style={styles.cartItemDetails}>
                            <div style={styles.cartItemName}>{item.title}</div>
                            <div style={styles.cartItemSku}>
                              SKU: {item.sku}
                            </div>
                            <div style={styles.cartItemPrice}>
                              GHC {item.price.toFixed(2)}
                            </div>
                          </div>
                        </div>

                        <div style={styles.cartActions}>
                          <button
                            style={styles.qtyBtn}
                            onClick={() =>
                              updateQuantity(
                                item.product_uuid,
                                item.quantity - 1,
                              )
                            }
                          >
                            <FontAwesomeIcon icon={faMinus} size={18} />
                          </button>
                          <input
                            type="number"
                            min="1"
                            value={item.quantity}
                            onChange={(e) =>
                              updateQuantity(item.product_uuid, e.target.value)
                            }
                            style={styles.qtyInput}
                          />
                          <button
                            style={styles.qtyBtn}
                            onClick={() =>
                              updateQuantity(
                                item.product_uuid,
                                item.quantity + 1,
                              )
                            }
                          >
                            <FontAwesomeIcon
                              icon={faPlus}
                              color="#1f1717"
                              size={18}
                            />
                          </button>
                          <button
                            style={styles.deleteBtn}
                            onClick={() => removeItem(item.product_uuid)}
                          >
                            <FontAwesomeIcon
                              icon={faTrash}
                              color="#FF4444"
                              size={18}
                            />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      <DashboardContentFooter />

      {/* Customer Modal */}
      {showCustomerModal && (
        <div className="modal">
          <div className="overlay"></div>
          <div className="modal-content">
            <div className="modal-body">
              <div className="modal-header">
                <p>Add New Customer</p>
                <button
                  className="close-modal"
                  onClick={() => setShowCustomerModal(false)}
                >
                  <FontAwesomeIcon icon={faClose} />
                </button>
              </div>
              <form
                onSubmit={handleAddNewCustomer}
                className="edit-patient-form"
              >
                <div style={styles.formGroupWrapper}>
                  <div className="form-group">
                    <label htmlFor="customer-name">Full Name *</label>
                    <input
                      type="text"
                      placeholder="Enter Customer Full Name"
                      value={newCustomerForm.name}
                      onChange={(e) =>
                        setNewCustomerForm({
                          ...newCustomerForm,
                          name: e.target.value,
                        })
                      }
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="customer-contact">Contact Number</label>
                    <input
                      type="text"
                      placeholder="Enter Customer Contact Number"
                      value={newCustomerForm.phone}
                      onChange={(e) =>
                        setNewCustomerForm({
                          ...newCustomerForm,
                          phone: e.target.value,
                        })
                      }
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label htmlFor="customer-emailaddress">Email Address</label>
                  <input
                    type="email"
                    placeholder="Enter Customer Email Address"
                    value={newCustomerForm.email}
                    onChange={(e) =>
                      setNewCustomerForm({
                        ...newCustomerForm,
                        email: e.target.value,
                      })
                    }
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="customer-address">Location Address</label>
                  <input
                    type="text"
                    placeholder="Enter Customer Address"
                    value={newCustomerForm.address}
                    onChange={(e) =>
                      setNewCustomerForm({
                        ...newCustomerForm,
                        address: e.target.value,
                      })
                    }
                  />
                </div>
                <div className="submitGroup">
                  <button
                    type="submit"
                    disabled={loading}
                    style={styles.modalSaveBtn}
                  >
                    {loading ? (
                      <FontAwesomeIcon icon={faSpinner} spin={true} />
                    ) : (
                      "Add Customer"
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Success Modal */}
      {modalSuccessMsg && (
        <MessageModal
          leftIcon={faCheckCircle}
          color={remainingBalance === 0 ? "green" : "orange"}
          message={successMessage}
          onClose={() => {
            setModalSuccessMsg(false);
            resetForm();
          }}
          rightIcon={faClose}
        />
      )}

      {modalError && (
        <MessageModal
          leftIcon={faCancel}
          color="red"
          message={errorMessage}
          onClose={() => setModalError(false)}
          rightIcon={faClose}
        />
      )}
    </div>
  );
}

const styles = {
  container: {
    width: "100%",
    height: "100%",
    backgroundColor: " rgb(236, 242, 250)",
    overflowX: "auto",
    WebkitOverflowScrolling: "touch",
    padding: "34px 32px 4rem 32px",
  },
  mainContainer: {
    display: "flex",
    flexDirection: "row",
    gap: "12px",
    marginTop: "20px",
  },
  leftColumn: {
    flex: 1,
    backgroundColor: "#FFF",
    borderRadius: "16px",
    padding: "20px",
    boxShadow: "0 2px 4px rgba(0,0,0,0.05)",
    border: "1px solid #E0E0E0",
    order: 2,
  },
  rightColumn: {
    flex: 1.5,
    display: "flex",
    flexDirection: "column",
    gap: "12px",
    order: 1,
  },
  section: {
    marginBottom: "20px",
  },
  label: {
    fontSize: "14px",
    fontWeight: "600",
    color: "#333",
    marginBottom: "6px",
    display: "block",
  },
  input: {
    width: "100%",
    padding: "16px 10px",
    borderRadius: "10px",
    border: "1px solid #E0E0E0",
    fontSize: "16px",
    backgroundColor: "#F5F5F5",
    outline: "none",
    transition: "border 0.3s",
  },
  grid2cols: {
    display: "grid",
    gridTemplateColumns: "repeat(2, 1fr)",
    gap: "12px",
  },
  rowWithButton: {
    display: "flex",
    gap: "8px",
    position: "relative",
  },
  searchWrapper: {
    flex: 1,
    position: "relative",
  },
  iconButton: {
    width: "40px",
    height: "40px",
    backgroundColor: "#000000",
    borderRadius: "10px",
    border: "1px solid #000000",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    transition: "all 0.3s",
  },
  searchResults: {
    position: "absolute",
    top: "100%",
    left: 0,
    right: 0,
    backgroundColor: "#FFF",
    borderRadius: "10px",
    border: "1px solid #E0E0E0",
    maxHeight: "250px",
    overflowY: "auto",
    zIndex: 1000,
    boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
    marginTop: "4px",
  },
  searchItem: {
    padding: "12px",
    borderBottom: "1px solid #F0F0F0",
    cursor: "pointer",
    transition: "background 0.2s",
  },
  searchItemName: {
    fontSize: "16px",
    fontWeight: "600",
    color: "#333",
    marginBottom: "4px",
  },
  searchItemPhone: {
    fontSize: "16px",
    color: "#666",
    letterSpacing: "1px",
  },
  productSearchHeader: {
    position: "relative",
    marginBottom: "16px",
    display: "flex",
    alignItems: "center",
  },
  searchIconWrapper: {
    position: "absolute",
    left: "12px",
    zIndex: 1,
  },
  searchIcon: {
    color: "#3D3D3D",
    fontSize: "18px",
  },
  productSearchInput: {
    width: "100%",
    padding: "16px 16px 16px 40px",
    borderRadius: "10px",
    border: "1px solid #E0E0E0",
    fontSize: "16px",
    backgroundColor: "#F5F5F5",
    outline: "none",
    transition: "all 0.3s",
  },
  clearSearchBtn: {
    position: "absolute",
    right: "12px",
    background: "none",
    border: "none",
    cursor: "pointer",
    color: "#999",
    padding: "4px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  row2cols: {
    display: "grid",
    gridTemplateColumns: "repeat(2, 1fr)",
    gap: "12px",
    marginBottom: "20px",
  },
  blueBox: {
    // Style for amount paid box
  },
  orangeBox: {
    // Style for discount box
  },
  boxLabel: {
    fontSize: "14px",
    fontWeight: "600",
    color: "#3d3d3d",
    marginBottom: "6px",
    display: "block",
  },
  numberInputAmount: {
    width: "100%",
    backgroundColor: "#3d69a2",
    borderRadius: "8px",
    padding: "12px 8px",
    fontSize: "26px",
    color: "#FFFFFF",
    fontWeight: "bold",
    border: "1px solid #E0E0E0",
    letterSpacing: "1px",
    outline: "none",
  },
  numberInputDiscount: {
    width: "100%",
    backgroundColor: "#ff9800",
    borderRadius: "8px",
    padding: "12px 8px",
    fontSize: "26px",
    color: "#FFFFFF",
    fontWeight: "bold",
    border: "1px solid #E0E0E0",
    letterSpacing: "1px",
    outline: "none",
  },
  summaryCard: {
    backgroundColor: "#F9F9F9",
    borderRadius: "12px",
    padding: "12px",
    marginBottom: "20px",
    border: "1px solid #E0E0E0",
  },
  summaryRow: {
    display: "flex",
    justifyContent: "space-between",
    marginBottom: "8px",
    fontSize: "14px",
  },
  price: {
    fontWeight: "500",
    letterSpacing: "1px",
  },
  totalRow: {
    borderTop: "1px solid #E0E0E0",
    paddingTop: "8px",
    marginTop: "4px",
    marginBottom: "4px",
  },
  totalLabel: {
    fontWeight: "bold",
    fontSize: "14px",
  },
  totalValue: {
    fontWeight: "900",
    fontSize: "16px",
    color: "#1976d2",
    letterSpacing: "1px",
  },
  balanceRow: {
    borderTop: "1px solid #E0E0E0",
    paddingTop: "8px",
    marginTop: "4px",
  },
  balanceLabel: {
    fontWeight: "bold",
  },
  balanceDue: {
    color: "#FF4444",
    fontWeight: "900",
    fontSize: "16px",
    letterSpacing: "1px",
  },
  balancePaid: {
    color: "#4CAF50",
    fontWeight: "900",
    fontSize: "16px",
    letterSpacing: "1px",
  },
  actionRow: {
    display: "flex",
    gap: "12px",
    marginTop: "20px",
  },
  cancelBtn: {
    flex: 1,
    backgroundColor: "#6c757d",
    borderRadius: "10px",
    padding: "12px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "6px",
    border: "none",
    cursor: "pointer",
    transition: "all 0.3s",
  },
  checkoutBtn: {
    flex: 1,
    backgroundColor: "#148918",
    borderRadius: "10px",
    padding: "12px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "6px",
    border: "none",
    cursor: "pointer",
    transition: "all 0.3s",
  },
  checkoutText: {
    color: "#FFF",
    fontSize: "14px",
    fontWeight: "600",
  },
  btnText: {
    color: "#FFF",
    fontSize: "14px",
    fontWeight: "600",
  },
  disabled: {
    opacity: 0.6,
    cursor: "not-allowed",
  },
  productsSection: {
    backgroundColor: "#FFF",
    borderRadius: "16px",
    padding: "16px",
    border: "1px solid #E0E0E0",
  },
  productsGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(4, 1fr)",
    gap: "8px",
    maxHeight: "280px",
    overflowY: "auto",
  },
  productCard: {
    margin: "4px",
    borderRadius: "12px",
    overflow: "hidden",
    cursor: "pointer",
    transition: "transform 0.2s",
    boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
    backgroundColor: "#fff",
  },
  productImageContainer: {
    width: "100%",
    height: "200px",
    position: "relative",
    backgroundColor: "#f5f5f5",
  },
  productImage: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
  },
  outOfStockOverlay: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(0, 0, 0, 0.7)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: "12px",
  },
  outOfStockText: {
    color: "#FF4444",
    fontWeight: "bold",
    fontSize: "14px",
    backgroundColor: "rgba(255, 255, 255, 0.9)",
    padding: "4px 8px",
    borderRadius: "4px",
  },
  productContent: {
    position: "absolute",
    bottom: 0,
    left: 0,
    right: 0,
    padding: "12px",
    background: "linear-gradient(to top, rgba(0,0,0,0.8), rgba(0,0,0,0))",
    color: "#FFF",
  },
  productName: {
    fontSize: "14px",
    fontWeight: "bold",
    marginBottom: "4px",
    textShadow: "1px 1px 2px rgba(0,0,0,0.75)",
  },
  productSku: {
    fontSize: "12px",
    color: "#DDD",
    marginBottom: "8px",
    textShadow: "1px 1px 1px rgba(0,0,0,0.5)",
  },
  productFooter: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  productPrice: {
    fontSize: "16px",
    fontWeight: "bold",
    color: "#FF6B35",
    textShadow: "1px 1px 2px rgba(0,0,0,0.5)",
  },
  productStock: {
    fontSize: "12px",
    color: "#CCC",
    marginTop: "2px",
  },
  addToCartBtn: {
    background: "#FFF",
    border: "none",
    borderRadius: "50%",
    width: "32px",
    height: "32px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    transition: "transform 0.2s",
  },
  noProducts: {
    textAlign: "center",
    padding: "40px",
    color: "#999",
    fontSize: "14px",
  },
  cartSection: {
    backgroundColor: "#FFF",
    borderRadius: "16px",
    padding: "20px",
    border: "1px solid #E0E0E0",
    maxHeight: "400px",
    display: "flex",
    flexDirection: "column",
  },
  cartHeader: {
    fontSize: "16px",
    fontWeight: "bold",
    marginBottom: "10px",
    color: "#333",
  },
  emptyCart: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    padding: "40px",
  },
  emptyText: {
    marginTop: "8px",
    color: "#999",
    fontSize: "18px",
  },
  cartItemsList: {
    flex: 1,
    overflowY: "auto",
  },
  cartItem: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "12px 0",
    borderBottom: "1px solid #F0F0F0",
  },
  cartItemInfo: {
    flex: 1,
    display: "flex",
    alignItems: "center",
    gap: "12px",
  },
  cartImage: {
    width: 60,
    height: 60,
    borderRadius: "8px",
    overflow: "hidden",
    backgroundColor: "#f5f5f5",
  },
  cartImageImg: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
  },
  cartItemDetails: {
    flex: 1,
  },
  cartItemName: {
    fontSize: "16px",
    fontWeight: "600",
    marginBottom: "4px",
  },
  cartItemSku: {
    fontSize: "14px",
    color: "#999",
    marginBottom: "2px",
  },
  cartItemPrice: {
    fontSize: "16px",
    fontWeight: "bold",
    color: "#FF6B35",
  },
  cartActions: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
  },
  qtyBtn: {
    width: "28px",
    height: "28px",
    backgroundColor: "#F0F0F0",
    borderRadius: "14px",
    border: "none",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    transition: "all 0.2s",
  },
  qtyInput: {
    width: "50px",
    textAlign: "center",
    padding: "6px",
    borderRadius: "4px",
    border: "1px solid #E0E0E0",
    fontSize: "16px",
    fontWeight: "bold",
  },
  deleteBtn: {
    background: "none",
    border: "none",
    cursor: "pointer",
    padding: "4px",
  },
  formGroupWrapper: {
    display: "flex",
    gap: "10px",
    marginBottom: "10px",
  },
  modalSaveBtn: {
    backgroundColor: "#3D3D3D",
    color: "#FFF",
    padding: "12px 24px",
    border: "none",
    borderRadius: "8px",
    cursor: "pointer",
    fontSize: "14px",
    fontWeight: "600",
  },
};
